/**
 * Bosnian strings for the notification centre, the bell's panel and the
 * profile card that decides what arrives.
 *
 * The base every other language is measured against. The words the bell had
 * before this screen existed — the title, "Danas", "Juče", the unread count —
 * stay in shell.ts, where the frame has always looked for them.
 *
 * Type names and their descriptions are NOT here. They come from the server in
 * the person's language, next to the switches they label, so the e-mail and
 * the switch that controls it cannot call the same thing by two names.
 */

export const notifications = {
  // ---- Counts -------------------------------------------------------
  // Neuter, agreeing with *obavještenje*: 1 hitno / 2 hitna / 5 hitnih.
  'notifications.high.one': '{count} hitno',
  'notifications.high.few': '{count} hitna',
  'notifications.high.many': '{count} hitnih',

  // ---- One notification -------------------------------------------
  // The button when the server knows where to go but sent no wording for it.
  'notifications.open': 'Otvori',
  'notifications.markRead': 'Označi kao pročitano',
  'notifications.delete': 'Obriši obavještenje',
  'notifications.deleted': 'Obavještenje je obrisano.',
  'notifications.failed': 'Nije uspjelo. Pokušajte ponovo.',
  'notifications.importance.high': 'Hitno',

  // ---- The bell's panel --------------------------------------------
  'notifications.viewAll': 'Sva obavještenja',
  'notifications.settings': 'Postavke obavještenja',

  // ---- The notification centre --------------------------------------
  'notifications.page.subtitle': 'Sve što vam je aplikacija javila, po danima.',
  'notifications.filter.label': 'Vrsta obavještenja',
  'notifications.filter.sve': 'Sve',
  'notifications.filter.prijave': 'Prijave',
  'notifications.filter.zadaci': 'Zadaci',
  'notifications.filter.5s': '5S',
  'notifications.filter.podsjetnici': 'Podsjetnici',
  'notifications.filter.sazetak': 'Sažetak',
  'notifications.unreadOnly': 'Samo nepročitana',
  'notifications.loadMore': 'Učitaj još',
  'notifications.shown': 'Prikazano {shown} od {total}',

  /*
   * Empty states say what *will* arrive here, not only that nothing has.
   * "Nema obavještenja" on its own reads the same as a broken screen; a list of
   * what would show up tells somebody new what the screen is for, and tells
   * somebody filtering whether they picked the right chip.
   */
  'notifications.empty.sve.title': 'Još nema obavještenja',
  'notifications.empty.sve':
    'Ovdje stižu hitne prijave, promjene na vašim prijavama, zadaci koji vam se dodijele i podsjetnici za ono što kasni.',
  'notifications.empty.unread': 'Novo obavještenje vidjet ćete ovdje i na zvoncu.',
  'notifications.empty.filter.title': 'Nema obavještenja ove vrste',
  'notifications.empty.prijave':
    'Ovdje stižu hitne prijave, promjene statusa na vašim prijavama i ocjene vaših prijedloga.',
  'notifications.empty.zadaci': 'Ovdje stižu zadaci koji vam se dodijele.',
  'notifications.empty.5s':
    'Ovdje stiže podsjetnik kad mašine u vašim pogonima ovog mjeseca još nemaju 5S ocjenu.',
  'notifications.empty.podsjetnici':
    'Ovdje stiže jutarnji pregled zadataka i prijava kojima je prošao rok.',
  'notifications.empty.sazetak': 'Ovdje stiže dnevni sažetak, ako ga uključite u postavkama.',
  'notifications.choose': 'Izaberite šta primate',

  // ---- Preferences, on the profile ---------------------------------
  'notifications.prefs.title': 'Obavještenja',
  'notifications.prefs.intro': 'Izaberite šta primate i gdje. Svaka promjena se čuva odmah.',
  'notifications.prefs.type': 'Vrsta',
  'notifications.prefs.app': 'U aplikaciji',
  'notifications.prefs.mail': 'E-pošta',
  // The accessible name of one switch: "Hitne prijave: E-pošta".
  'notifications.prefs.switch': '{type}: {channel}',
  'notifications.prefs.noEmail': 'Za e-poštu prvo upišite svoju adresu u dijelu „Kontakt".',
  // One sentence, replacing the server's description on that row: the hour
  // and the "only when there is something" are what people ask about.
  'notifications.prefs.dailySummary':
    'Svako jutro u 7 sati: šta je novo i šta kasni u vašim pogonima — samo kad ima nešto za reći.',
  'notifications.prefs.saved': 'Sačuvano: {type}',
  'notifications.prefs.failed': 'Promjena nije sačuvana. Pokušajte ponovo.',
  'notifications.prefs.none': 'Za vašu ulogu nema obavještenja koja se mogu podesiti.',
} as const

export type NotificationsSlice = Record<keyof typeof notifications, string>
